import { Box, Button, Grid, Typography } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useGlobals } from 'src/shared/contexts/appContext';
import { createInvoiceAddressOnMember } from 'src/shared/services/memberService';
import { InvoiceAddressCreateForm } from './invoiceAddressCreateForm';
import { useSchemaCreateInvoiceAddressWithReference } from '../schemas/useSchemaCreateInvoiceAddressWithReference';

type InvoiceAddressHandleModalCreateProps = {
  modalOpen: boolean;
  onClose: () => void;
  onStartOver: () => void;
  memberId: number;
};

export const InvoiceAddressHandleModalCreate = (props: InvoiceAddressHandleModalCreateProps) => {
  const { modalOpen, onClose, onStartOver, memberId } = props;

  const isMounted = useIsMounted();

  const { setDateStamp } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);

  const addInvoiceAddressRequestSchema = useSchemaCreateInvoiceAddressWithReference();
  type AddInvoiceAddressFormInterface = InferType<typeof addInvoiceAddressRequestSchema>;

  const formMethods = useForm<AddInvoiceAddressFormInterface>({
    resolver: yupResolver(addInvoiceAddressRequestSchema),
    defaultValues: {
      name: '',
      address: '',
      postalcode: '',
      city: '',
      country: '',
      address2: '',
      email: '',
      name2: '',
      invoiceReference: '',
    },
  });

  const { handleSubmit } = formMethods;

  const onSubmit = useCallback(
    async (data: AddInvoiceAddressFormInterface) => {
      setLoading(true);
      try {
        const res = await createInvoiceAddressOnMember(memberId, data);
        if (isMounted()) {
          ConsoleHelper.log('RES', res);
          toast.success(t('member:invoiceAddress.saveSuccess'));
          setDateStamp(new Date());
          onClose();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [memberId, isMounted, onClose, setDateStamp],
  );

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={() => onClose()}
      onSave={() => ConsoleHelper.log('save')}
      dialogTitle={t('member:invoiceAddress.createModalHeader')}
      padding={0}
      maxWidth='sm'
      dialogContent={
        <Box
          sx={{
            position: 'relative',
            minHeight: '15rem',
            p: 4,
          }}
        >
          <LoadingOverlay loading={loading} />
          <Box sx={{}}>
            <GridWrapper>
              <Grid item xs={12}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography>{t('member:invoiceAddress.enterReference')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField name='invoiceReference' required label={t('member:invoiceAddress.reference')} />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={2}>
                      <Grid item xs={12}>
                        <Typography>{t('member:invoiceAddress.pageHeader')}</Typography>
                      </Grid>
                      <InvoiceAddressCreateForm />
                    </Grid>
                  </form>
                </FormProvider>
              </Grid>
            </GridWrapper>
          </Box>
        </Box>
      }
      dialogActions={
        <Box display='flex' flexDirection='row' justifyContent='flex-end' width='100%'>
          <Button disableElevation variant='outlined' color='primary' onClick={onStartOver} disabled={loading} sx={{ mr: 1 }}>
            {t('common:button.startOver')}
          </Button>
          <Button disableElevation variant='contained' color='primary' onClick={onHandleSave} disabled={loading}>
            {t('common:button.save')}
          </Button>
        </Box>
      }
    />
  );
};
